import * as React from 'react';
import classnames from 'classnames';

import Spinner from 'components/spinner/Spinner';

import Link from 'libs/router/Link';

import { expandDataProps } from 'utils/helper/common';

import './action-button.scss';

import type {
  ActionButtonColor,
  ActionButtonHeight,
  ActionButtonStyle,
  ActionButtonWidth,
  Data,
  PromiseAction
} from 'types';

type Button = {
  [x: string]: unknown
};

type Props = {
  children: React.ReactNode,
  link: string,
  type: string,
  disabled?: boolean,
  color: ActionButtonColor,
  height: ActionButtonHeight,
  width: ActionButtonWidth,
  style: ActionButtonStyle,
  newTab?: boolean,
  title?: string,
  data?: Data,
  customSize?: {
    width?: number | string,
    height?: number | string
  },
  className: string,
  disabledWithSpinner?: boolean,
  onClick?: (event: any) => PromiseAction | Promise<void> | void | undefined | null
};

class ActionButton extends React.PureComponent<Props> {
  static defaultProps = {
    color: 'secondary',
    type: 'button',
    width: 'normal',
    height: 'normal',
    style: 'normal',
    className: '',
    link: ''
  };

  getNewTabButtonProps() {
    return this.props.newTab ? {
      target: '_blank',
      rel: 'noopener noreferrer'
    } : {};
  }

  getButtonProps(): Button {
    const { color, width, height, style, disabled, type, title, customSize, className, onClick } = this.props;
    const buttonProps: Button = {
      className: classnames({
        'action-button': true,
        [`action-button--color-${color}`]: color,
        [`action-button--width-${width}`]: width,
        [`action-button--height-${height}`]: height,
        [`action-button--style-${style}`]: style,
        'action-button--disabled': disabled,
        [className]: Boolean(className)
      })
    };

    if (disabled) {
      buttonProps.disabled = true;
    }

    if (onClick && !disabled) {
      buttonProps.onClick = onClick;
    }

    if (type) {
      buttonProps.type = type;
    }

    if (title) {
      buttonProps.title = title;
    }

    if (customSize) {
      buttonProps.style = {
        width: customSize.width,
        height: customSize.height
      };
    }

    return buttonProps;
  }

  renderButtonContent() {
    const { children } = this.props;

    return (
      <div className="action-button__inner">
        {children}
      </div>
    );
  }

  renderMailtoButton(): React.ReactNode {
    const { data, link } = this.props;

    return (
      <a
        href={link}
        {...this.getButtonProps()}
        {...expandDataProps(data)}
      >
        {this.renderButtonContent()}
      </a>
    );
  }

  renderHttpButton(): React.ReactNode {
    const { data, link } = this.props;

    return (
      <a
        href={
          link.indexOf('http') === 0 ?
            link :
            `http://${link}`
        }
        rel="noopener noreferrer"
        target="_blank"
        {...this.getButtonProps()}
        {...expandDataProps(data)}
      >
        {this.renderButtonContent()}
      </a>
    );
  }

  renderLinkButton(): React.ReactNode {
    const { data, link } = this.props;

    if (link.startsWith('mailto:')) {
      return this.renderMailtoButton();
    }

    if (link.startsWith('http')) {
      return this.renderHttpButton();
    }

    return (
      <Link
        to={link}
        {...this.getButtonProps()}
        {...expandDataProps(data)}
        {...this.getNewTabButtonProps()}
      >
        {this.renderButtonContent()}
      </Link>
    );
  }

  renderSpinner(): React.ReactNode {
    return (
      <div className="action-button__spinner">
        <Spinner
          position="inline"
          size="tiny"
          color="light"
        />
      </div>
    );
  }

  render(): React.ReactNode {
    const { data, link, disabled, disabledWithSpinner } = this.props;

    if (link) {
      return this.renderLinkButton();
    }

    return (
      <button // eslint-disable-line react/button-has-type
        {...this.getButtonProps()}
        {...expandDataProps(data)}
      >
        {this.renderButtonContent()}
        {(disabled && disabledWithSpinner) && this.renderSpinner()}
      </button>
    );
  }
}

export default ActionButton;
